/* eslint-disable i18next/no-literal-string -- a code-only module: URL fragments and response fields; it renders nothing */
// What GoTrue actually answered to a session refresh (offline contract C2,
// Codex r1 #14, r2 #5-6). auth-js removes its stored session on ANY
// non-retryable refresh error (user_banned and unknown codes included) and
// only says SIGNED_OUT, so session disappearance cannot prove a terminal
// rejection. The Supabase transport (accountChannel.fencedFetch) reports
// every refresh response here; the offline runtime classifies it against
// the contract's terminal allowlist.
//
//  - Captured from the FIRST request on (the Supabase client starts
//    refreshing at import, long before the runtime boots): answers that
//    arrive with no consumer are buffered and delivered when it installs.
//  - Bound to its request: the refresh token it sent, the account that
//    token belonged to (read from the stored session before the request
//    left), the SESSION GENERATION and the owner incarnation remembered
//    then. The generation moves whenever a session is established or
//    replaced (any 2xx from the token endpoint, a live session the runtime
//    adopts) or the owner departs (C3). An answer is authoritative only
//    while its generation and incarnation are still current AND the stored
//    session is that token or gone (`refreshAnswerCurrent`). Absence of a
//    stored session alone never proves it (r3 #6): a newer session that was
//    established and later removed moved the generation. What can not be
//    proven is not applied (the owner stays refresh_pending).
// Plain module state, no browser global.

export interface StoredRefresh {
  /** The stored session's user id, lowercased. */
  subject: string | null
  refreshToken: string | null
}

/** What the request carried, captured before it left. */
export interface RefreshRequestBinding {
  /** The account (uuid, lowercased) whose stored session held `token`, or null (unknown). */
  owner: string | null
  token: string | null
  /** The session generation when it left (-1: not captured, never applied). */
  gen?: number
  /** The owner incarnation when it left (null: the runtime had not started). */
  incarnation?: number | null
}

export interface RefreshAnswer extends RefreshRequestBinding {
  /** HTTP status; 0 when no response arrived. */
  status: number
  /** GoTrue's error_code (else the legacy `error`), null on success or none. */
  code: string | null
}

const BUFFER_MAX = 16
let sink: ((a: RefreshAnswer) => void) | null = null
let buffer: RefreshAnswer[] = []
let reader: (() => StoredRefresh | null | 'unknown') | null = null
let incarnationReader: (() => number | null) | null = null
let generation = 0

/** A session was established or replaced, or the owner departed: older answers stop speaking for it. */
export function bumpSessionGeneration(): void {
  generation += 1
}

export function sessionGeneration(): number {
  return generation
}

/** The owner incarnation now (registered by the runtime). */
export function setIncarnationReader(fn: (() => number | null) | null): void {
  incarnationReader = fn
}

function incarnationNow(): number | null {
  try {
    return incarnationReader ? incarnationReader() : null
  } catch {
    return null
  }
}

/** The stored session, read synchronously (registered by the Supabase client's owner). */
export function setStoredRefreshReader(fn: (() => StoredRefresh | null | 'unknown') | null): void {
  reader = fn
}

/** null: no session stored; 'unknown': cannot tell. */
export function readStoredRefresh(): StoredRefresh | null | 'unknown' {
  if (!reader) return 'unknown'
  try {
    return reader()
  } catch {
    return 'unknown'
  }
}

/** Install the consumer; answers that arrived before it are delivered now, in order. */
export function setRefreshObserver(fn: ((a: RefreshAnswer) => void) | null): void {
  sink = fn
  if (!fn) return
  const pending = buffer
  buffer = []
  for (const a of pending) fn(a)
}

/** The token endpoint with grant_type=refresh_token. */
export function isRefreshRequest(url: string): boolean {
  return url.includes('/auth/v1/token') && url.includes('grant_type=refresh_token')
}

/** Any token grant (password, refresh, PKCE, OTP): a 2xx establishes a session. */
export function isTokenRequest(url: string): boolean {
  return url.includes('/auth/v1/token')
}

/** The binding of a refresh request, from its body and the stored session (before it leaves). */
export function captureRefreshRequest(body: unknown): RefreshRequestBinding {
  let token: string | null = null
  if (typeof body === 'string') {
    try {
      const o = JSON.parse(body) as { refresh_token?: unknown }
      if (typeof o.refresh_token === 'string' && o.refresh_token) token = o.refresh_token
    } catch {
      token = null
    }
  }
  const stored = readStoredRefresh()
  const owner = stored && stored !== 'unknown' && token && stored.refreshToken === token ? stored.subject : null
  return { owner, token, gen: generation, incarnation: incarnationNow() }
}

/**
 * The answer still speaks for the session it was about: the same account
 * as `currentOwnerUid`, and the stored session either gone or still holding
 * the token that was refused (never a newer one).
 */
export function refreshAnswerCurrent(b: RefreshRequestBinding, currentOwnerUid: string | null): boolean {
  if (!b.owner || !b.token || !currentOwnerUid || b.owner !== currentOwnerUid.toLowerCase()) return false
  // The session it was about is still the current one (r3 #6).
  if (typeof b.gen !== 'number' || b.gen !== generation) return false
  if (b.incarnation !== null && b.incarnation !== undefined && b.incarnation !== incarnationNow()) return false
  const stored = readStoredRefresh()
  if (stored === 'unknown') return false
  if (stored === null) return true
  if (stored.subject && stored.subject !== b.owner) return false
  return stored.refreshToken === null || stored.refreshToken === b.token
}

function codeOf(body: unknown): string | null {
  if (!body || typeof body !== 'object') return null
  const o = body as { error_code?: unknown; code?: unknown; error?: unknown }
  if (typeof o.error_code === 'string') return o.error_code
  if (typeof o.code === 'string') return o.code
  if (typeof o.error === 'string') return o.error
  return null
}

function deliver(a: RefreshAnswer): void {
  if (sink) {
    sink(a)
    return
  }
  buffer.push(a)
  if (buffer.length > BUFFER_MAX) buffer = buffer.slice(-BUFFER_MAX)
}

/** Report one refresh response with its request's binding (never throws, never consumes the caller's body). */
export function observeRefreshResponse(res: Response | null, req: RefreshRequestBinding = { owner: null, token: null, gen: -1 }): void {
  if (!res) {
    deliver({ ...req, status: 0, code: null })
    return
  }
  if (res.ok) {
    // A new session: every answer bound to an older one is obsolete.
    bumpSessionGeneration()
    deliver({ ...req, status: res.status, code: null })
    return
  }
  let copy: Response
  try {
    copy = res.clone()
  } catch {
    deliver({ ...req, status: res.status, code: null })
    return
  }
  void copy.json().then(b => deliver({ ...req, status: res.status, code: codeOf(b) }), () => deliver({ ...req, status: res.status, code: null }))
}

/** Test seam. */
export function __resetRefreshObserverForTests(): void {
  sink = null
  buffer = []
  reader = null
  incarnationReader = null
  generation = 0
}
